import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, LogOut, Settings, ChevronDown, Home as HomeIcon, Info, Mail, List, Flag, Coffee, Shield, FileText, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import InstallPWA from '@/components/InstallPWA';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import NotificationBell from '@/components/NotificationBell';

export default function TopBar({ user, login, logout, site, onMyPins, onAccount, onSelectPin }) {
  const { t } = useTranslation();
  const isStaff = user && ['moderator', 'admin', 'owner'].includes(user.role);
  return (
    <div className="absolute top-0 left-0 right-0 z-[600] p-4 sm:p-6 flex justify-between items-start pointer-events-none">
      <div className="pointer-events-auto" data-testid="logo-dropdown-wrapper">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="logo-trigger" className="glass-panel rounded-2xl sm:rounded-3xl px-3 py-2 sm:px-5 sm:py-3 flex items-center gap-1.5 sm:gap-2 hover:-translate-y-0.5 transition">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-brand" />
              <div className="font-display font-black text-lg sm:text-2xl tracking-tight text-left">
                <span className="text-brand-ink">{site.logo_text_left}</span>
                <span className="text-brand">{site.logo_text_right}</span>
              </div>
              <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-brand-dim sm:ml-1" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="rounded-2xl glass-panel min-w-[180px]">
            <DropdownMenuItem data-testid="nav-home" asChild>
              <Link to="/"><HomeIcon className="w-4 h-4 mr-2" /> {t('nav.home')}</Link>
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="nav-about" asChild>
              <Link to="/about"><Info className="w-4 h-4 mr-2" /> {t('nav.about')}</Link>
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="nav-tutorial" asChild>
              <Link to="/tutorial"><BookOpen className="w-4 h-4 mr-2" /> How it works</Link>
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="nav-contact" asChild>
              <Link to="/contact"><Mail className="w-4 h-4 mr-2" /> {t('nav.contact')}</Link>
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="nav-privacy" asChild>
              <Link to="/privacy"><Shield className="w-4 h-4 mr-2" /> Privacy</Link>
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="nav-terms" asChild>
              <Link to="/terms"><FileText className="w-4 h-4 mr-2" /> Terms</Link>
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="nav-donate" asChild>
              <a href="https://buymeacoffee.com/curbalert" target="_blank" rel="noopener noreferrer">
                <Coffee className="w-4 h-4 mr-2" /> {t('nav.donate')}
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <LanguageSwitcher />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="pointer-events-auto flex items-center gap-3">
        <InstallPWA />
        {user && <NotificationBell onSelectPin={onSelectPin} />}
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-btn" className="glass-panel rounded-full pl-2 pr-4 py-2 flex items-center gap-2 hover:-translate-y-0.5 transition">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-semibold">
                    {user.name?.[0] || 'U'}
                  </div>
                )}
                <span className="font-medium text-sm hidden sm:inline">{user.name?.split(' ')[0]}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-2xl glass-panel">
              <DropdownMenuLabel className="font-display">{user.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-my-pins" onClick={onMyPins}>
                <List className="w-4 h-4 mr-2" /> {t('nav.myPins')}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-account" onClick={onAccount}>
                <Settings className="w-4 h-4 mr-2" /> {t('nav.account')}
              </DropdownMenuItem>
              {isStaff && (
                <DropdownMenuItem data-testid="menu-admin" asChild>
                  <Link to="/admin"><Settings className="w-4 h-4 mr-2" /> {t('nav.admin')}</Link>
                </DropdownMenuItem>
              )}
              {isStaff && (
                <DropdownMenuItem data-testid="menu-moderation" asChild>
                  <Link to="/admin/moderation"><Flag className="w-4 h-4 mr-2" /> {t('nav.moderation')}</Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <LanguageSwitcher />
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                <LogOut className="w-4 h-4 mr-2" /> {t('nav.signOut')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            data-testid="signin-btn"
            onClick={login}
            className="hidden sm:inline-flex rounded-full bg-brand hover:bg-brand-hover text-white px-6 py-5 shadow-lg"
          >
            {t('common.signInGoogle')}
          </Button>
        )}
      </div>
    </div>
  );
}
